import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck, Package, Mail, AlertCircle, RefreshCw, ShoppingBag, Send } from 'lucide-react';
import { authService } from '../auth/authService';
import { profileService, Profile, TelegramWidgetUser } from './profileService';
import { orderService } from '../orders/orderService';
import { Order } from '../orders/typres';
import { Button } from '@/components/ui/button';
import OrderCard from '../orders/OrderCard';
import { isMiniApp } from '../../telegram';
import TelegramLoginWidget from '../../components/TelegramLoginWidget';

type Tab = 'profile' | 'orders';

const getInitials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

const ProfilePage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('profile');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  // Telegram linking (website only)
  const [tgLinked, setTgLinked] = useState(false);
  const [tgError, setTgError] = useState<string | null>(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [pendingMerge, setPendingMerge] = useState<{ user: TelegramWidgetUser; otherOrders: number } | null>(null);

  const handleTgAuth = async (user: TelegramWidgetUser, confirm = false) => {
    setTgError(null);
    setTgBusy(true);
    try {
      await profileService.linkTelegram(user, confirm);
      setTgLinked(true);
      setPendingMerge(null);
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.status === 'confirm_required') {
        setPendingMerge({ user, otherOrders: d.otherOrders ?? 0 });
      } else if (d?.status === 'conflict') {
        setTgError(d.message || 'К аккаунту уже привязан другой Telegram.');
      } else {
        setTgError('Не удалось привязать Telegram. Попробуйте ещё раз.');
      }
    } finally {
      setTgBusy(false);
    }
  };

  const fetchOrders = () => {
    setOrdersLoading(true);
    setOrdersError(null);
    orderService.getOrders()
      .then(r => setOrders(r.orders))
      .catch(() => setOrdersError('Не удалось загрузить заказы'))
      .finally(() => setOrdersLoading(false));
  };

  useEffect(() => {
    profileService.getProfile()
      .then(({ profile: p }) => { setProfile(p); setTgLinked(!!p.telegramLinked); })
      .catch(e => setProfileError(e instanceof Error ? e.message : 'Ошибка загрузки'));

    fetchOrders();
  }, []);

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'profile', label: 'Профиль' },
    { key: 'orders', label: 'Заказы', count: orders.length },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-3xl">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-foreground sm:text-5xl">Профиль</h1>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex gap-1 rounded-[18px] border border-foreground/8 bg-foreground/[0.03] p-1">
          {tabs.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-[14px] py-2.5 text-sm font-medium transition-all ${
                tab === t.key
                  ? 'bg-foreground/10 text-foreground shadow-sm'
                  : 'text-foreground/40 hover:text-foreground/70'
              }`}
            >
              {t.label}
              {t.count != null && t.count > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-all ${
                  tab === t.key ? 'bg-foreground/10 text-foreground/70' : 'bg-foreground/[0.06] text-foreground/30'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Profile tab ── */}
        {tab === 'profile' && (
          <div className="space-y-4">
            {profileError && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-200">
                {profileError}
              </div>
            )}

            <div className="rounded-[28px] border border-foreground/10 bg-card p-6 shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
              {profile ? (
                <div className="flex flex-col gap-5">
                  {/* Avatar + name */}
                  <div className="flex items-center gap-4">
                    {profile.avatar ? (
                      <img
                        src={profile.avatar}
                        alt={profile.username}
                        className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-foreground/10"
                      />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#991b1b,#dc2626)] text-xl font-bold text-white ring-2 ring-foreground/10">
                        {getInitials(profile.username)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-xl font-semibold text-foreground">{profile.username}</p>
                      {profile.adminRole && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-600/20 px-2 py-0.5 text-[11px] font-medium text-red-600 dark:text-red-300">
                          <ShieldCheck className="h-3 w-3" />
                          Администратор
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info fields */}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="flex items-center gap-3 rounded-2xl border border-foreground/8 bg-foreground/[0.04] px-4 py-3">
                      <Mail className="h-4 w-4 shrink-0 text-foreground/30" />
                      <div className="min-w-0">
                        <p className="text-[11px] text-foreground/40">Почта</p>
                        <p className="truncate text-sm font-medium text-foreground">{profile.email || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 rounded-2xl border border-foreground/8 bg-foreground/[0.04] px-4 py-3">
                      <Package className="h-4 w-4 shrink-0 text-foreground/30" />
                      <div>
                        <p className="text-[11px] text-foreground/40">Заказов</p>
                        <p className="text-sm font-medium text-foreground">{orders.length}</p>
                      </div>
                    </div>
                  </div>

                  {/* Telegram linking — website only (in the mini-app auth is already Telegram) */}
                  {!isMiniApp() && (
                    <div className="rounded-2xl border border-foreground/8 bg-foreground/[0.04] p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
                        <Send className="h-4 w-4 text-sky-400" />
                        Telegram
                      </div>
                      {tgLinked ? (
                        <p className="text-sm text-foreground/55">
                          Аккаунт привязан к Telegram — можно входить и через бота, и по почте.
                        </p>
                      ) : pendingMerge ? (
                        <div className="space-y-3">
                          <p className="text-sm text-foreground/70">
                            Найден отдельный Telegram-аккаунт
                            {pendingMerge.otherOrders > 0 ? ` (заказов: ${pendingMerge.otherOrders})` : ''}. Объединить его
                            с текущим? Заказы, избранное и корзина будут перенесены, второй аккаунт удалён.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleTgAuth(pendingMerge.user, true)}
                              disabled={tgBusy}
                              variant="destructive"
                              className="rounded-2xl px-5 py-2.5 text-sm"
                            >
                              Объединить
                            </Button>
                            <Button
                              onClick={() => setPendingMerge(null)}
                              disabled={tgBusy}
                              variant="ghost"
                              className="rounded-2xl border border-foreground/10 bg-foreground/5 px-5 py-2.5 text-sm text-foreground/60"
                            >
                              Отмена
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm text-foreground/55">
                            Привяжите Telegram, чтобы входить и через бота, и по почте.
                          </p>
                          <TelegramLoginWidget botId={7915168955} label="Привязать Telegram" onAuth={(u) => handleTgAuth(u)} />
                          {tgError && <p className="text-xs text-red-600 dark:text-red-300">{tgError}</p>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 border-t border-foreground/8 pt-2">
                    {profile.adminRole && (
                      <Link
                        to="/admin"
                        className="inline-flex items-center gap-2 rounded-2xl border border-red-500/30 bg-red-600/20 px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-300 transition hover:bg-red-600/30"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Панель администратора
                      </Link>
                    )}
                    <Button
                      onClick={async () => { await authService.logout(); navigate('/login'); }}
                      variant="ghost"
                      className="inline-flex items-center gap-2 rounded-2xl border border-foreground/10 bg-foreground/5 px-5 py-2.5 text-sm font-medium text-foreground/60 transition hover:border-foreground/20 hover:text-foreground"
                    >
                      <LogOut className="h-4 w-4" />
                      Выйти
                    </Button>
                  </div>
                </div>
              ) : !profileError ? (
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 shrink-0 animate-pulse rounded-full bg-foreground/5" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 w-40 animate-pulse rounded-xl bg-foreground/5" />
                    <div className="h-3 w-24 animate-pulse rounded-xl bg-foreground/5" />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Orders tab ── */}
        {tab === 'orders' && (
          <div>
            {ordersError && (
              <div className="mb-4 flex items-center gap-3 rounded-[20px] border border-red-500/25 bg-red-500/10 p-4 text-red-600 dark:text-red-300">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-sm">{ordersError}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchOrders}
                  className="rounded-full border border-red-500/25 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                >
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  Повторить
                </Button>
              </div>
            )}

            {ordersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-28 animate-pulse rounded-[24px] bg-foreground/5" />
                ))}
              </div>
            ) : !ordersError && orders.length === 0 ? (
              <div className="rounded-[32px] border border-foreground/10 bg-card py-20 text-center shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-foreground/10 bg-foreground/5">
                  <ShoppingBag className="h-8 w-8 text-foreground/25" />
                </div>
                <h2 className="mb-2 text-xl font-semibold text-foreground">Заказов пока нет</h2>
                <p className="mb-8 text-sm text-foreground/40">Оформите первый заказ, и он появится здесь</p>
                <Button asChild className="rounded-full bg-red-600 px-8 hover:bg-red-700">
                  <Link to="/">Перейти в каталог</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map(order => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfilePage;
